// MA-891 — per-badge progress for the rings and stage dots, and the single badge
// closest to being earned (KID-JOURNEY-2026-10-08 D-KJ-20, D-KJ-21). Drawn, never
// spoken. Pure.

import type { SkillStage } from "./rules";
import { PRACTICE_DAY_THRESHOLDS, REPEATING_DAY_THRESHOLDS } from "./ids";

/** Stage dots of a skill badge (started, silver, gold): how many are filled. */
export function stageDots(stage: SkillStage): 0 | 1 | 2 | 3 {
  return stage === "gold" ? 3 : stage === "silver" ? 2 : stage === "started" ? 1 : 0;
}

export interface DayRing {
  threshold: number;
  /** Segment count, or null for a continuous arc (30, 50, 100). */
  segments: number | null;
  /** Filled segments (or, for an arc, practice days toward the threshold). */
  filled: number;
  /** 0..1 */
  fraction: number;
}

/** The ring of the practice-day badge `days:<threshold>` at `count` practice days:
 *  3 segments for days:3, 7 for days:7 (one per practice day since its previous
 *  instance), 7 for days:14 (one per two practice days since its previous instance),
 *  a continuous arc for 30, 50 and 100. A one-time badge's ring counts toward it. */
export function dayRing(threshold: number, count: number): DayRing {
  if (REPEATING_DAY_THRESHOLDS.has(threshold)) {
    const since = count % threshold;
    const perSegment = threshold / 7;
    const filled = Math.floor(since / perSegment);
    return { threshold, segments: 7, filled, fraction: filled / 7 };
  }
  if (threshold >= 30) {
    const filled = Math.min(count, threshold);
    return { threshold, segments: null, filled, fraction: filled / threshold };
  }
  const filled = Math.min(count, threshold);
  return { threshold, segments: threshold, filled, fraction: filled / threshold };
}

/** Practice days since the most recent day on which ANY practice-day badge (or a
 *  repeat instance) landed; equals `count` before the first one. */
export function practiceDaysSincePreviousDayBadge(count: number): number {
  let last = 0;
  for (let d = 1; d <= count; d++) {
    const lands = PRACTICE_DAY_THRESHOLDS.some((n) => (REPEATING_DAY_THRESHOLDS.has(n) ? d % n === 0 : d === n));
    if (lands) last = d;
  }
  return count - last;
}

export interface ClosestCandidate {
  id: string;
  family: "skill" | "chapter" | "days";
  /** 0..1 progress toward the next earn of this badge. */
  fraction: number;
  /** True when there is nothing left to earn on it (gold skill, held one-time medal). */
  done: boolean;
  /** MA-898 (RED round 1 MUST-2): a practice-day badge the child already holds at least
   *  once (only the repeating 7- and 14-day badges can be held and still not done). */
  held?: boolean;
}

/** The progress fraction of a skill badge's NEXT stage: started 1/3, silver 2/3. */
export function skillFraction(stage: SkillStage): number {
  return stageDots(stage) / 3;
}

/** The single badge the peek shows (D-KJ-21, RED round 3 NIT-R3-5): the pal's Next-up
 *  skill badge when that skill is a visible, not-yet-gold candidate; ONLY when the child
 *  has no such Next-up skill, the nearest practice-day badge (largest ring fraction,
 *  earlier candidate on ties, except that a never-held badge wins a tie over a held repeat:
 *  MA-898 RED round 1 MUST-2, so 13 practice days picks the first 14-day badge, not the
 *  held 7-day one at the same 6/7), never another skill. Never a day badge over a Next-up
 *  skill, so the peek is not a come-back-tomorrow nudge. A gold Next-up skill has nothing
 *  left to earn, so it counts as "no Next-up skill" and the day badge shows (operator
 *  ruling D-1008-22). Null when there is neither. */
export function closestBadge(candidates: readonly ClosestCandidate[], nextUpSkillId: string | null | undefined): ClosestCandidate | null {
  if (nextUpSkillId) {
    const skill = candidates.find((c) => c.family === "skill" && c.id === `skill:${nextUpSkillId}` && !c.done);
    if (skill) return skill;
  }
  let best: ClosestCandidate | null = null;
  for (const c of candidates) {
    if (c.done || c.family !== "days") continue;
    if (best === null || c.fraction > best.fraction || (c.fraction === best.fraction && best.held === true && c.held !== true)) best = c;
  }
  return best;
}
